import React, { useEffect, useState } from 'react';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CustomerProps, OrderProfileCardProps } from '@helpers/Models';
import { getFormattedPhoneNumber } from '@services/OrderCreationService';
import { useNavigate, useLocation } from 'react-router-dom';
import { FrontendRoutes } from '@helpers/Constants/Navigation';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { CustomerOrderService } from '@services/CustomerService';

export const OrderProfileCard: React.FC<OrderProfileCardProps> = ({
  customerName,
  phoneHome,
}) => {
  const [customer, setCustomer] = useState<CustomerProps | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { showConfirmationToast } = UseConfirmationToast();

  // Determine the title based on current route
  const getTitle = (): string => {
    if (location.pathname === '/create-order/order/summary') {
      return 'Order Summary';
    }
    return 'New Order';
  };

  useEffect(() => {
    const customerDetails = localStorage.getItem('customerDetails');
    if (customerDetails) {
      const parsedCustomerDetails = JSON.parse(customerDetails);
      setCustomer(parsedCustomerDetails);
    }
  }, []);

  const discardOrder = (): void => {
    // Get the current order placing customer's order data and remove it
    const customerId = localStorage.getItem('customerId');
    const currentCustomerId = customerId ?? 0;

    const parsedOrderData = CustomerOrderService.getParsedOrderData();
    delete parsedOrderData[currentCustomerId];
    CustomerOrderService.setParsedOrderData(parsedOrderData);

    // Remove the current customizing id of the order
    localStorage.removeItem('currentCustomizingId');

    // Remove the selected active speciesName
    localStorage.removeItem('speciesName');

    // Redirect the user to the customer list.
    navigate(FrontendRoutes.CUSTOMER_LIST);
  };

  return (
    <>
      {/* Displaying title for order creation module */}
      {!customerName && !phoneHome && (
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold rounded-md ml-2">
            {getTitle()}
          </h2>
          <button
            type="button"
            className="text-[#DF1A0C] font-bold ml-2"
            onClick={() =>
              showConfirmationToast(
                'Are you sure you want to discard your Order? This action cannot be undone.',
                'Discard Order',
                'delete',
                discardOrder,
                'Discard Order',
                undefined,
                'Cancel',
              )
            }
          >
            <FontAwesomeIcon icon={faTrashCan} className="mr-2" />
            Discard Order
          </button>
        </div>
      )}
      <div className="mt-4 rounded-md bg-[#DFDFD7]">
        <div className="p-4">
          <div className="flex justify-between items-center bg-[#DFDFD7]">
            <div className="flex items-center space-x-4">
              <div>
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-2">
                    <h2 className="text-xl font-medium">
                      {customerName ??
                        `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}`}
                    </h2>
                  </div>
                </div>
                {phoneHome ? (
                  <p className="text-gray-500 text-sm">
                    Phone number:
                    {getFormattedPhoneNumber(phoneHome ?? '')}
                  </p>
                ) : (
                  <p className="text-gray-500 text-sm">
                    Phone number:
                    {getFormattedPhoneNumber(customer?.phoneHome ?? '')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-4">
              <div className="bg-white p-2 rounded shadow-md border border-gray-300">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0.666104 2.83594C0.666104 1.45523 1.78539 0.335938 3.1661 0.335938H14.8328C16.2135 0.335938 17.3328 1.45523 17.3328 2.83594V3.65634C17.3329 3.66445 17.3329 3.67256 17.3328 3.68067V11.1693C17.3328 12.55 16.2135 13.6693 14.8328 13.6693H3.1661C1.78539 13.6693 0.666104 12.55 0.666104 11.1693V3.68066C0.665986 3.67256 0.665986 3.66446 0.666104 3.65635V2.83594ZM2.33277 5.22637V11.1693C2.33277 11.6295 2.70587 12.0026 3.1661 12.0026H14.8328C15.293 12.0026 15.6661 11.6295 15.6661 11.1693V5.22637L10.3862 8.74631C9.54644 9.30614 8.45244 9.30614 7.61269 8.74631L2.33277 5.22637ZM15.6661 3.22328L9.46169 7.35956C9.18177 7.54617 8.8171 7.54617 8.53719 7.35956L2.33277 3.22328V2.83594C2.33277 2.3757 2.70587 2.0026 3.1661 2.0026H14.8328C15.293 2.0026 15.6661 2.3757 15.6661 2.83594V3.22328Z"
                    fill="#1F2937"
                  />
                </svg>
              </div>
              <div className="bg-white p-2 rounded shadow-md border border-gray-300">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0.666016 3.16406C0.666016 1.78335 1.7853 0.664062 3.16602 0.664062H5.89872C6.6161 0.664062 7.253 1.12311 7.47986 1.80368L8.72803 5.54822C8.99032 6.33507 8.6341 7.19505 7.89225 7.56597L6.7618 8.1312C7.50064 9.42172 8.57502 10.4961 9.86554 11.235L10.4308 10.1045C10.8017 9.36264 11.6617 9.00643 12.4485 9.26871L16.1931 10.5169C16.8736 10.7437 17.3327 11.3806 17.3327 12.098V14.8307C17.3327 16.2114 16.2134 17.3307 14.8327 17.3307H13.9993C6.63555 17.3307 0.666016 11.3612 0.666016 3.9974V3.16406ZM3.16602 2.33073C2.70578 2.33073 2.33268 2.70383 2.33268 3.16406V3.9974C2.33268 10.4407 7.55603 15.6641 13.9993 15.6641H14.8327C15.2929 15.6641 15.666 15.291 15.666 14.8307V12.098L11.9215 10.8499L10.981 12.7309C10.7809 13.1309 10.3008 13.3017 9.89308 13.1179C7.67054 12.1158 5.8809 10.3262 4.87883 8.10366C4.695 7.69594 4.86581 7.2158 5.26584 7.01579L7.14689 6.07526L5.89872 2.33073H3.16602Z"
                    fill="#1F2937"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
